import {
  CalendarIcon,
  CheckIcon,
  Cross2Icon,
  EnvelopeClosedIcon,
  HomeIcon,
  MobileIcon,
  Pencil1Icon,
  PersonIcon,
} from "@radix-ui/react-icons";
import {
  ApiError,
  Auth0AccountRole,
  InviteType,
  InvitesService,
  ParentsService,
  StudentsService,
  TutorsService,
} from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import { Button, ButtonColor } from "components/Button";
import IconBubble, { BubbleTone } from "components/IconBubble";
import { TagColor, TagSize } from "components/Tag";
import { Tag } from "components/Tag/TagChip";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import moment from "moment";
import ArchivePersonButton from "pages/manage/ProfilePageResources/ArchivePersonButton";
import {
  KeyboardEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PhoneInput from "react-phone-input-2";
import { DatePicker } from "rsuite";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import "./index.css";

/** What the card shows and edits of a student, parent or tutor. */
export type DetailsPerson = {
  id: number;
  org_id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone_number?: string | null;
  birth_date?: string | null;
  address_line_1?: string | null;
  archived_at?: string | null;
};

type Field = "name" | "email" | "phone" | "birthday" | "address";
const FIELDS: Field[] = ["name", "email", "phone", "birthday", "address"];

const LABEL: Record<Field, string> = {
  name: "Name",
  email: "Email",
  phone: "Phone",
  birthday: "Birthday",
  address: "Address",
};

const ICON: Record<Field, ReactNode> = {
  name: <PersonIcon />,
  email: <EnvelopeClosedIcon />,
  phone: <MobileIcon />,
  birthday: <CalendarIcon />,
  address: <HomeIcon />,
};

const TONE: Record<Field, BubbleTone> = {
  name: BubbleTone.GREEN,
  email: BubbleTone.SKY,
  phone: BubbleTone.GREEN,
  birthday: BubbleTone.ORANGE,
  address: BubbleTone.INDIGO,
};

/** Each kind's save path (today's update endpoints) and invite. */
const KINDS = {
  [PersonKind.STUDENT]: {
    noun: "student",
    invite: InviteType.STUDENT,
    role: Auth0AccountRole.ME,
    sendsPhone: true,
    save: (id: number, requestBody: object) =>
      StudentsService.updateStudent({ studentId: id, requestBody }),
  },
  [PersonKind.PARENT]: {
    noun: "parent",
    invite: InviteType.PARENT,
    role: Auth0AccountRole.PARENT,
    sendsPhone: false,
    save: (id: number, requestBody: object) =>
      ParentsService.updateParent({ parentId: id, requestBody }),
  },
  [PersonKind.TUTOR]: {
    noun: "tutor",
    invite: InviteType.TUTOR,
    role: Auth0AccountRole.ORG_TUTOR,
    sendsPhone: true,
    save: (id: number, requestBody: object) =>
      TutorsService.updateTutor({ tutorId: id, requestBody }),
  },
};

// When the server refuses a value, what to say.
const INVALID: Partial<Record<Field, string>> = {
  email: "Enter a valid email.",
  phone: "Enter a valid phone number.",
};

/** The editable text of each field, as the inputs hold it. */
type Draft = {
  first: string;
  last: string;
  email: string;
  phone: string;
  birthday: string;
  address: string;
};

function draftOf(person: DetailsPerson): Draft {
  return {
    first: person.first_name ?? "",
    last: person.last_name ?? "",
    email: person.email ?? "",
    phone: person.phone_number ?? "",
    birthday: person.birth_date ?? "",
    address: person.address_line_1 ?? "",
  };
}

/** "(617) 555-0142" for a US number; anything else as stored. */
function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const us =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (us.length !== 10) return phone;
  return `(${us.slice(0, 3)}) ${us.slice(3, 6)}-${us.slice(6)}`;
}

/** A field's value as shown, or "" when empty. */
function shown(field: Field, person: DetailsPerson): string {
  switch (field) {
    case "name":
      return [person.first_name, person.last_name].filter(Boolean).join(" ");
    case "email":
      return person.email ?? "";
    case "phone":
      return person.phone_number && person.phone_number !== "1"
        ? formatPhone(person.phone_number)
        : "";
    case "birthday":
      return person.birth_date
        ? moment(person.birth_date, "YYYY-MM-DD").format("MMM D, YYYY")
        : "";
    case "address":
      return person.address_line_1 ?? "";
  }
}

/**
 * What saving one field sends (only that field), or what to fix first.
 * Phone, birthday and address keep today's rules: an empty one is cleared.
 */
function requestFor(field: Field, draft: Draft): object | string {
  switch (field) {
    case "name":
      return draft.first.trim() && draft.last.trim()
        ? { first_name: draft.first.trim(), last_name: draft.last.trim() }
        : "Enter a first and last name.";
    case "email": {
      const email = draft.email.trim();
      if (email && !email.includes("@")) return INVALID.email as string;
      return { email: email || null };
    }
    case "phone":
      return {
        phone_number: draft.phone && draft.phone !== "1" ? draft.phone : null,
      };
    case "birthday":
      return { birth_date: draft.birthday || null };
    case "address":
      return { address_line_1: draft.address.trim() || null };
  }
}

/**
 * A person's details in the Icons look: name, email, phone, birthday and
 * address, each with its tinted icon. Click a value to edit it in place:
 * Enter or ✓ saves just that field through today's update endpoint, Esc or
 * ✕ puts it back, and clicking another row saves the open one first. An
 * archived person's details are read-only. The header keeps Account Linked
 * or Invite; the footer keeps Archive.
 */
export default function DetailsCard<P extends DetailsPerson>({
  kind,
  person,
  onChange,
}: {
  kind: PersonKind;
  person: P;
  /** The person as saved (a field, or archiving). */
  onChange: (person: P) => void;
}) {
  const { account, currently_selected_role } = useContext(
    OrgRolesAndAccountContext
  );
  const [editing, setEditing] = useState<Field>();
  const [draft, setDraft] = useState<Draft>(() => draftOf(person));
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState<Field>();
  const [inviting, setInviting] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const editable = !person.archived_at;
  const hasAccount = Boolean((person as { account?: unknown }).account);
  const { noun } = KINDS[kind];

  function open(field: Field) {
    setDraft(draftOf(person));
    setError(undefined);
    setEditing(field);
  }

  function cancel() {
    setDraft(draftOf(person));
    setError(undefined);
    setEditing(undefined);
  }

  /** Saves the open field; false while it still needs fixing. */
  async function save(): Promise<boolean> {
    if (!editing) return true;
    const requestBody = requestFor(editing, draft);
    if (typeof requestBody === "string") {
      setError(requestBody);
      return false;
    }
    const unchanged = Object.entries(requestBody).every(
      ([key, value]) => (person[key as keyof P] ?? null) === value
    );
    if (unchanged) {
      cancel();
      return true;
    }
    setSaving(true);
    try {
      const updated = await KINDS[kind].save(person.id, requestBody);
      onChange({ ...person, ...updated });
      const field = editing;
      setEditing(undefined);
      setError(undefined);
      setJustSaved(field);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setJustSaved(undefined), 2000);
      return true;
    } catch (e) {
      const status = (e as ApiError)?.status;
      setError(
        (status === 422 && INVALID[editing]) || "Couldn't save. Try again."
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Opens a field; one already open is saved first.
  async function choose(field: Field) {
    if (!editable || editing === field || saving) return;
    if (!editing || (await save())) open(field);
  }

  function onEditKey(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      save();
    } else if (event.key === "Escape") {
      event.stopPropagation();
      cancel();
    }
  }

  function invite() {
    setInviting(true);
    InvitesService.inviteUser({
      requestBody: {
        first_name: person.first_name,
        last_name: person.last_name,
        phone_number:
          KINDS[kind].sendsPhone && person.phone_number
            ? person.phone_number
            : undefined,
        email: person.email as string,
        org_invited_by: person.org_id,
        acct_invited_by: account?.reference_id as string,
        type: KINDS[kind].invite,
        user_id: person.id,
      },
      role: KINDS[kind].role,
      createEntity: false,
      createAccount: true,
    })
      .then(() =>
        Notifications.success(
          `${noun[0].toUpperCase()}${noun.slice(1)} reinvited!`
        )
      )
      .catch((e: ApiError) => {
        Notifications.error(`Unable to reinvite ${noun}`);
        console.error(`Error (#${e.status}): ${e.message}`);
      })
      .finally(() => setInviting(false));
  }

  const input = (
    value: string,
    onValue: (value: string) => void,
    label: string,
    autoFocus = true
  ) => (
    <input
      className="details-card__input"
      aria-label={label}
      aria-invalid={!!error}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onValue(e.target.value)}
    />
  );

  function editor(field: Field) {
    const set = (key: keyof Draft) => (value: string) =>
      setDraft((d) => ({ ...d, [key]: value }));
    switch (field) {
      case "name":
        return (
          <span className="details-card__pair">
            {input(draft.first, set("first"), "First name")}
            {input(draft.last, set("last"), "Last name", false)}
          </span>
        );
      case "email":
        return input(draft.email, set("email"), "Email");
      case "phone":
        return (
          <PhoneInput
            country={"us"}
            value={draft.phone}
            onChange={set("phone")}
            prefix="+"
            containerClass="details-card__phone"
            inputClass="details-card__input"
            inputProps={{ "aria-label": "Phone", autoFocus: true }}
            placeholder="+1 (555) 555-5555"
            dropdownStyle={{ display: "none" }}
            countryCodeEditable={false}
          />
        );
      case "birthday":
        return (
          <DatePicker
            oneTap
            defaultValue={
              draft.birthday
                ? moment(draft.birthday, "YYYY-MM-DD").toDate()
                : undefined
            }
            onChange={(value) =>
              set("birthday")(value ? moment(value).format("YYYY-MM-DD") : "")
            }
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== "Escape") e.preventDefault();
            }}
            format="MMM d, yyyy"
            cleanable={true}
            className="details-card__date"
            aria-label="Birthday"
            block
          />
        );
      case "address":
        return input(draft.address, set("address"), "Address");
    }
  }

  return (
    <section className="details-card">
      <div className="details-card__header">
        <h2 className="details-card__title">Details</h2>
        {!hasAccount &&
        [Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
          currently_selected_role as Auth0AccountRole
        ) ? (
          <Tooltip>
            <TooltipTrigger>
              <Button
                color={!person.email ? ButtonColor.GRAY : ButtonColor.PURPLE}
                onClick={invite}
                disabled={inviting || !person.email}
                extraClasses="w-20 h-7"
              >
                Invite
              </Button>
            </TooltipTrigger>
            {!person.email && (
              <TooltipContent>
                <p>An email must be added to send an account creation invite</p>
              </TooltipContent>
            )}
          </Tooltip>
        ) : hasAccount ? (
          <Tag
            size={TagSize.NARROW}
            color={TagColor.BLUE}
            item="Account Linked"
          />
        ) : null}
      </div>

      <div className="details-card__rows">
        {FIELDS.map((field) => {
          const isEditing = editing === field;
          const value = shown(field, person);
          return (
            <div
              key={field}
              className={`details-card__row${
                isEditing ? " details-card__row--editing" : ""
              }${editable && !isEditing ? " details-card__row--editable" : ""}`}
              role={editable && !isEditing ? "button" : undefined}
              tabIndex={editable && !isEditing ? 0 : undefined}
              onClick={() => choose(field)}
              onKeyDown={(e) => {
                if (!isEditing && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  choose(field);
                }
              }}
            >
              <IconBubble tone={TONE[field]}>{ICON[field]}</IconBubble>
              <div className="details-card__body">
                <small className="details-card__label">{LABEL[field]}</small>
                {isEditing ? (
                  <div className="details-card__edit" onKeyDown={onEditKey}>
                    <div className="details-card__fields">{editor(field)}</div>
                    <button
                      type="button"
                      className="details-card__ok"
                      aria-label="Save"
                      disabled={saving}
                      onClick={(e) => {
                        e.stopPropagation();
                        save();
                      }}
                    >
                      <CheckIcon />
                    </button>
                    <button
                      type="button"
                      className="details-card__cancel"
                      aria-label="Cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        cancel();
                      }}
                    >
                      <Cross2Icon />
                    </button>
                  </div>
                ) : value ? (
                  <span className="details-card__value">{value}</span>
                ) : (
                  <span className="details-card__empty">
                    Add {LABEL[field].toLowerCase()}
                  </span>
                )}
                {isEditing && error && (
                  <p className="details-card__error" role="alert">
                    {error}
                  </p>
                )}
              </div>
              {!isEditing &&
                editable &&
                (justSaved === field ? (
                  <CheckIcon
                    className="details-card__saved"
                    aria-label="Saved"
                  />
                ) : (
                  <Pencil1Icon className="details-card__pen" aria-hidden />
                ))}
            </div>
          );
        })}
      </div>

      <div className="details-card__footer">
        <span className="details-card__hint">
          {editable ? "Click a value to edit it" : ""}
        </span>
        {/* Archive (and Delete, when it comes) side by side. */}
        <div className="details-card__actions">
          <ArchivePersonButton
            kind={kind}
            person={person}
            onChanged={(archived_at) => onChange({ ...person, archived_at })}
          />
        </div>
      </div>
    </section>
  );
}
